import { useEffect, useState } from "react";
import type { FocusEvent } from "react";

import { useForm } from "react-hook-form";

import { useQueryClient } from "@tanstack/react-query";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

import Card from "../../shared/Card/Card";
import { ILead, PaymentStatus, LeadPaymentMethod } from "../../../models/Lead";
import classNames from "../../../utils/classNames";
import usePayments from "../../../data/usePayments";
import useLeadMutations from "../../../data/useLeadMutations";
import { useAuth } from "../../../contexts/AuthContext";
import PaymentBubble from "../../../utils/paymentBubble";
import { TextInput } from "../../shared/Inputs/TextInput";
import SelectInput from "../../shared/Inputs/SelectInput";
import ConfirmModal from "../../shared/Confirm/ConfirmModal";
import useNotification from "../../notifications/useNotifications";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { CheckboxInput } from "../../shared/Inputs/CheckboxInput";
import useSingleSetting from "../../../data/useSingleSetting";
import PaymentStatusCell from "../../payments/PaymentStatusCell";
import PriceFormat from './../../../utils/priceFormat';

type FormValues = {
  price: number;
  commission: number;
  accountName: string;
  paymentReference: string;
  accountNumber: string;
  sortCode: string;
  chequeNumber: string;
  applyVAT: boolean;
  paymentMethod: LeadPaymentMethod;
};

export default function LeadPaymentDetails({ lead }: { lead: ILead }) {
  const { singleSetting } = useSingleSetting("PaymentRefPrefix");
  const { update } = useLeadMutations(lead.id, "LeadPaymentDetails");
  const {
    sendPayment,
    syncPayment,
    paymentStatus: getPaymentStatus,
  } = usePayments(false, undefined, undefined, true);
  const { saveHandlers } = useDefaultCRUDHandlers("Payment Details");
  const { claims } = useAuth();
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();
  const {
    register,
    getValues,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isDirty, isSubmitting },
  } =
    useForm<FormValues>({
      defaultValues: {
        price: lead.price,
        commission: lead.commission,
        accountName: lead.accountName ?? "",
        paymentReference: lead.paymentReference ?? "",
        accountNumber: lead.accountNumber ?? "",
        sortCode: lead.sortCode ?? "",
        chequeNumber: lead.chequeNumber ?? "",
        applyVAT: lead.applyVAT,
        paymentMethod: lead.paymentMethod,
      },
    });
  const [paymentConfirmOpen, setPaymentConfirmOpen] = useState(false);
  const [rotateChevron, setRotateChevron] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(lead.paymentStatus);

  // Payment method options for the SelectInput
  const paymentMethodOptions = [
    { id: LeadPaymentMethod["Bank Transfer"], name: "Bank Transfer" },
    { id: LeadPaymentMethod["Cheque"], name: "Cheque" },
    { id: LeadPaymentMethod["Cash"], name: "Cash" },
  ];

  const handleRotate = () => setRotateChevron(!rotateChevron);

  const rotate = rotateChevron ? "rotate(360deg)" : "rotate(0)";

  const onSubmit = (data: any, saveNotification: boolean) => {
    const updatedLead = {
      ...lead,
      ...data,
      price: data.price && parseFloat(data.price),
      commission: data.commission && parseFloat(data.commission),
    };

    if (saveNotification === true) {
      update.mutate(updatedLead, saveHandlers);
    } else {
      update.mutate(updatedLead);
    }

    reset({
      price: data.price,
      commission: data.commission,
      accountName: data.accountName!,
      paymentReference: data.paymentReference!,
      accountNumber: data.accountNumber!,
      sortCode: data.sortCode!,
      chequeNumber: data.chequeNumber!,
      applyVAT: data.applyVAT,
      paymentMethod: data.paymentMethod,
    });
  };

  const handlePayment = () => {
    handleSubmit((data) => onSubmit(data, false))();
    setPaymentConfirmOpen(true);
  };

  const sendPaymentRequest = () => {
    const data = getValues();

    if (data.price === 0) {
      addNotification({
        variant: "warning",
        primaryText: "Please make sure price is not zero",
      });
      return;
    }

    if (
      data.paymentMethod === LeadPaymentMethod["Bank Transfer"] &&
      (!data.accountName ||
        !data.accountNumber ||
        !data.sortCode)
    ) {
      addNotification({
        variant: "warning",
        primaryText: "Please fill in all payment details",
      });
      return;
    }

    sendPayment.mutate(lead.id, {
      onSuccess: (res: any) => {
        if (res.success) {
          setPaymentConfirmOpen(false);
          saveHandlers.onSuccess();
        } else {
          addNotification({
            variant: "error",
            primaryText: res.message,
          });
        }
      },
      onError: (e) => {
        console.log("error", e);
        saveHandlers.onError();
      },
    });
  };

  const saveOnBlur = (event: FocusEvent<HTMLFormElement>) => {
    const target = event.target as HTMLElement | null;
    if (
      !(target instanceof HTMLInputElement) &&
      !(target instanceof HTMLSelectElement) &&
      !(target instanceof HTMLTextAreaElement)
    ) {
      return;
    }

    const nextTarget = event.relatedTarget as HTMLElement | null;
    if (nextTarget?.getAttribute("type") === "submit") {
      return;
    }

    if (isDisabled || !isDirty || isSubmitting || update.isLoading) {
      return;
    }

    handleSubmit((data) => onSubmit(data, false))();
  };

  const handleRefresh = () => {
    handleRotate();

    syncPayment.mutate(lead.id, {
      onSuccess: (res: any) => {
        queryClient.refetchQueries(["lead", lead.id]);
        addNotification({
          variant: "success",
          primaryText: "Payment status synced",
        });
      },
      onError: (e) => {
        addNotification({
          variant: "error",
          primaryText: "Error syncing payment status",
        });
      },
    });
  };

  useEffect(() => {
    if (lead.paymentStatus !== 0 && lead.paymentStatus !== 3)
      setIsDisabled(true);
  }, [lead, lead.paymentStatus]);

  // Watch for changes in the price and applyVAT fields
  const [vatPrice, setVatPrice] = useState(0);

  const price = watch("price");
  const applyVAT = watch("applyVAT");
  const paymentMethod = watch("paymentMethod");

  useEffect(() => {
    // TODO: Make admin level variable
    if (applyVAT) setVatPrice(price * 1.2);
  }, [price]);

  useEffect(() => {
    if (applyVAT) setVatPrice(price * 1.2);
    else setVatPrice(0);
  }, [applyVAT]);

  useEffect(() => {
    if (singleSetting.isLoading) return;
    if (lead.paymentReference !== "" && lead.paymentReference !== null) return;
    if (singleSetting.data == null || singleSetting.data == undefined) return;
    setValue(
      "paymentReference",
      singleSetting.data?.data.value + lead.vehicle?.vrm
    );
  }, [singleSetting.isLoading]);

  // useEffect(() => {
  //   console.log("lead", lead.paymentStatus);
  //   if (lead.paymentStatus === PaymentStatus.Approved) {
  //     getPaymentStatus.mutate(
  //       { leadId: lead.id, transactionId: lead.tellerooTransactionId },
  //       {
  //         onSuccess: (res) => {
  //           console.log("res", res);
  //           setPaymentStatus(res.data.status);
  //         },
  //       }
  //     );
  //   } else {
  //     setPaymentStatus(lead.paymentStatus);
  //   }
  // }, []);

  return (
    <>
      <ConfirmModal
        open={paymentConfirmOpen}
        setOpen={setPaymentConfirmOpen}
        title="Raise payment?"
        confirmButtonText="Raise Payment"
        message={`Are you sure you want to raise this payment? \n The payment details will be locked until payment has been sent or cancelled.`}
        isLoading={false}
        onConfirm={sendPaymentRequest}
      />
      <Card bodyClassName="p-4">
        <h2 className="flex justify-between items-center text-lg font-medium pb-5 pt-2 leading-6 text-gray-900">
          <span>Payment Details</span>
          <span className="flex items-center gap-4">
            <div className="w-48">
              <SelectInput
                label=""
                registerName="paymentMethod"
                options={paymentMethodOptions}
                disabled={isDisabled}
                register={register}
                setValue={setValue}
                watch={watch}
                size="sm"
              />
            </div>
            <PaymentStatusCell lead={lead} />
          </span>
        </h2>
        <form
          onSubmit={handleSubmit((data) => onSubmit(data, true))}
          onBlurCapture={saveOnBlur}
          className="space-y-4 border-t border-gray-200"
        >
          <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-2">
            <TextInput
              label="Current Price (£)"
              registerName="price"
              disabled={isDisabled || !claims?.isAdmin}
              register={register}
            />
            <TextInput
              label="Commission (£)"
              registerName="commission"
              disabled={isDisabled || !claims?.isAdmin}
              register={register}
            />
            {paymentMethod === LeadPaymentMethod["Bank Transfer"] && (
              <TextInput
                label="Account Name"
                registerName="accountName"
                disabled={isDisabled}
                register={register}
              />
            )}

            <TextInput
              label="Payment Reference"
              registerName="paymentReference"
              disabled={isDisabled}
              register={register}
            />

            {paymentMethod === LeadPaymentMethod["Cheque"] && (
              <TextInput
                label="Cheque Number"
                registerName="chequeNumber"
                disabled={isDisabled}
                register={register}
              />
            )}

            {paymentMethod === LeadPaymentMethod["Bank Transfer"] && (
              <TextInput
                label="Account Number"
                registerName="accountNumber"
                disabled={isDisabled}
                register={register}
              />
            )}
            {paymentMethod === LeadPaymentMethod["Bank Transfer"] && (
              <TextInput
                label="Sort Code"
                registerName="sortCode"
                disabled={isDisabled}
                register={register}
              />
            )}

            <div className="flex gap-x-2 col-span-2">
              <CheckboxInput
                label={`Apply VAT `}
                registerName="applyVAT"
                disabled={isDisabled || !claims?.isAdmin}
                register={register}
              />
              {vatPrice !== 0 && (
                <span className="text-gray-500"> ({PriceFormat(vatPrice)})</span>
              )}
            </div>
          </div>

          <div className="flex justify-between">
            <button
              className={classNames(
                isDisabled
                  ? "bg-gray-600"
                  : "bg-primary-600 hover:bg-primary-700",
                "rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"
              )}
              disabled={isDisabled}
              type="submit"
            >
              Update
            </button>

            <button
              className={classNames(
                isDisabled
                  ? "bg-gray-600"
                  : "bg-primary-600 hover:bg-primary-700",
                "rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"
              )}
              disabled={isDisabled}
              type="button"
              onClick={() => handlePayment()}
            >
              Raise Payment
            </button>
          </div>
        </form>
      </Card>
    </>
  );
}
